import { usePdf } from '@mikecousins/react-pdf';
import { AnimatePresence } from 'framer-motion';
import { FC, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import ContentSpinner from '@/components/ContentSpinner';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useHasPermission from '@/hooks/useHasPermission';
import { InvoiceResource } from '@/types/resources';
import { InvoiceStatus } from '@/utils/enums';

import InvoicesChangeReceiverModal from './InvoicesChangeReceiverModal';
import InvoicesEditModal from './InvoicesEditModal';
import InvoicesMemberModal from './InvoicesMemberModal';

const ModalHeader = styled.div`
  height: 56px;
  background: rgba(0, 0, 0, 0.6);
  width: 100%;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledModal = styled(Modal)`
  display: block;
  padding: 0;
  background: rgba(0, 0, 0, 0.35);
`;

const ModalContent = styled.div`
  overflow-y: auto;
  text-align: center;
  max-height: calc(100vh - 56px);
  transition: justify-content ease-out 0.2s;
`;

const FileName = styled(Text)`
  display: flex;
  align-items: center;
  svg {
    margin-right: 8px;
  }
`;

const ButtonsHolder = styled.div`
  display: flex;
`;

const NavigationButton = styled.button`
  background-color: ${({ theme }) => theme.colors.gray};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 23px;
  width: 23px;
  border-radius: 50%;
  margin-left: 8px;
  transition: background-color ease-out 0.2s;
  &:hover {
    background-color: #b3b3b3;
  }
  svg {
    width: 14px;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    &:hover {
      background-color: ${({ theme }) => theme.colors.gray};
    }
  }
`;

const DownloadButton = styled.button`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
  `}
  font-weight: 700;
  font-size: 11px;
  line-height: 15px;
  color: ${({ theme }) => theme.colors.white};
  height: 23px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-radius: 100px;
  background-color: ${({ theme }) => theme.colors.gray};
  margin-left: 12px;
  transition: background-color ease-out 0.2s;
  svg {
    margin-right: 8px;
  }
  &:hover {
    background-color: #b3b3b3;
  }
`;

const EditInvoiceButton = styled(DownloadButton)``;

const CloseButton = styled(NavigationButton)`
  margin-left: 24px;
`;

const StyledCanvas = styled.canvas<{ $isEditMode: boolean; $width: number }>`
  transition: transform ease-out 0.2s;
  ${({ $isEditMode }) =>
    $isEditMode &&
    css`
      transform: translateX(-100px);
    `}
`;

interface Props {
  onClose: () => void;
  fileName: string;
  downloadLink: string;
  file: string;
  invoice?: InvoiceResource;
}

const InvoicePdfModal: FC<Props> = ({
  onClose,
  fileName,
  downloadLink,
  file,
  invoice,
}) => {
  const canManageInvoices = useHasPermission('manage invoices');

  const [isEditModel, setIsEditMode] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);

  const [showReceiverModal, setShowReceiverModal] = useState(false);

  const [showChangeReceiverModal, setShowChangeReceiverModal] = useState(false);

  const [scale, setScale] = useState(1.2);

  const [page, setPage] = useState(1);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { pdfDocument } = usePdf({
    file: file,
    page,
    canvasRef,
    scale,
  });

  useEffect(() => {
    const handleModalOnKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keyup', handleModalOnKeyUp, true);
    return () => {
      window.removeEventListener('keyup', handleModalOnKeyUp, true);
    };
  }, [onClose]);

  return (
    <>
      <StyledModal>
        <ModalHeader>
          <FileName $type="smallBold" $color="white">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="16" height="16" rx="4" fill="#1789D5" />
              <path
                d="M2 10.6799V5.67993H4.11111C4.15972 5.67993 4.22454 5.68225 4.30556 5.68688C4.38889 5.68919 4.46296 5.69614 4.52778 5.70771C4.82639 5.75401 5.0706 5.85239 5.26042 6.00285C5.45255 6.15331 5.59375 6.34313 5.68403 6.57229C5.77431 6.79914 5.81944 7.05262 5.81944 7.33271C5.81944 7.6128 5.77315 7.86743 5.68056 8.0966C5.59028 8.32345 5.44907 8.51211 5.25694 8.66257C5.06713 8.81303 4.82407 8.91141 4.52778 8.95771C4.46296 8.96697 4.38889 8.97391 4.30556 8.97854C4.22222 8.98317 4.15741 8.98549 4.11111 8.98549H2.94444V10.6799H2ZM2.94444 8.10354H4.06944C4.11806 8.10354 4.1713 8.10123 4.22917 8.0966C4.28704 8.09197 4.34028 8.08271 4.38889 8.06882C4.5162 8.0341 4.61458 7.97623 4.68403 7.89521C4.75347 7.81188 4.80093 7.72044 4.82639 7.6209C4.85417 7.51905 4.86806 7.42299 4.86806 7.33271C4.86806 7.24243 4.85417 7.14752 4.82639 7.04799C4.80093 6.94614 4.75347 6.8547 4.68403 6.77368C4.61458 6.69035 4.5162 6.63132 4.38889 6.5966C4.34028 6.58271 4.28704 6.57345 4.22917 6.56882C4.1713 6.56419 4.11806 6.56188 4.06944 6.56188H2.94444V8.10354Z"
                fill="white"
              />
              <path
                d="M6.5166 10.6799V5.67993H8.13466C8.17401 5.67993 8.25503 5.68109 8.37771 5.6834C8.5004 5.68572 8.61845 5.69382 8.73188 5.70771C9.13466 5.75632 9.47609 5.89637 9.75619 6.12785C10.0363 6.35933 10.2492 6.65331 10.3951 7.00979C10.5409 7.36627 10.6138 7.75632 10.6138 8.17993C10.6138 8.60354 10.5409 8.99359 10.3951 9.35007C10.2492 9.70655 10.0363 10.0005 9.75619 10.232C9.47609 10.4635 9.13466 10.6035 8.73188 10.6522C8.61845 10.666 8.5004 10.6741 8.37771 10.6765C8.25503 10.6788 8.17401 10.6799 8.13466 10.6799H6.5166ZM7.47494 9.79104H8.13466C8.19716 9.79104 8.28165 9.78989 8.38813 9.78757C8.49461 9.78294 8.59068 9.77252 8.67632 9.75632C8.89392 9.71234 9.071 9.61049 9.20757 9.45077C9.34646 9.29104 9.44831 9.09891 9.51313 8.87438C9.58026 8.64984 9.61382 8.41836 9.61382 8.17993C9.61382 7.92993 9.5791 7.69266 9.50966 7.46813C9.44253 7.24359 9.33952 7.05377 9.20063 6.89868C9.06174 6.74359 8.88697 6.64521 8.67632 6.60354C8.59068 6.58502 8.49461 6.57461 8.38813 6.57229C8.28165 6.56998 8.19716 6.56882 8.13466 6.56882H7.47494V9.79104Z"
                fill="white"
              />
              <path
                d="M11.4469 10.6799V5.67993H14.5024V6.62438H12.3913V7.70771H14.0858V8.65215H12.3913V10.6799H11.4469Z"
                fill="white"
              />
            </svg>
            {fileName}
          </FileName>
          <ButtonsHolder>
            {Boolean(pdfDocument && pdfDocument.numPages) && (
              <>
                <NavigationButton
                  disabled={page === 1}
                  onClick={() => setPage((prev) => prev - 1)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20.25 12H3.75"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10.5 5.25L3.75 12L10.5 18.75"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </NavigationButton>
                <NavigationButton
                  disabled={page === pdfDocument?.numPages}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.75 12H20.25"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.5 5.25L20.25 12L13.5 18.75"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </NavigationButton>
                <NavigationButton
                  onClick={() => setScale((prev) => prev - 0.2)}
                >
                  <svg
                    width="10"
                    height="2"
                    viewBox="0 0 10 2"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="10" height="1.5" fill="currentColor" />
                  </svg>
                </NavigationButton>
                <NavigationButton
                  onClick={() => setScale((prev) => prev + 0.2)}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="1"
                      y="5"
                      width="10"
                      height="1.5"
                      fill="currentColor"
                    />
                    <rect
                      x="5.25"
                      y="10.75"
                      width="10"
                      height="1.5"
                      transform="rotate(-90 5.25 10.75)"
                      fill="currentColor"
                    />
                  </svg>
                </NavigationButton>
              </>
            )}
            <DownloadButton as="a" href={downloadLink} download>
              <svg
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_4352_151214)">
                  <path
                    d="M2 7.28564L6 11.2856L10 7.28564"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 2.14282V11.2857"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_4352_151214">
                    <rect
                      width="12"
                      height="12"
                      fill="currentColor"
                      transform="translate(0 0.5)"
                    />
                  </clipPath>
                </defs>
              </svg>
              Download
            </DownloadButton>
            {!!invoice &&
              (canManageInvoices || invoice.status !== InvoiceStatus.Paid) && (
                <EditInvoiceButton
                  onClick={() => {
                    setIsEditMode((prev) => !prev);
                    setShowEditModal((prev) => !prev);
                  }}
                >
                  Edit invoice
                </EditInvoiceButton>
              )}
            {!!invoice && canManageInvoices && (
              <EditInvoiceButton
                onClick={() => {
                  setIsEditMode((prev) => !prev);
                  setShowReceiverModal((prev) => !prev);
                }}
              >
                Edit member
              </EditInvoiceButton>
            )}
            {!!invoice && canManageInvoices && (
              <EditInvoiceButton
                onClick={() => {
                  setIsEditMode((prev) => !prev);
                  setShowChangeReceiverModal((prev) => !prev);
                }}
              >
                Change Receiver & Type
              </EditInvoiceButton>
            )}
            <CloseButton onClick={onClose}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 18L6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CloseButton>
          </ButtonsHolder>
        </ModalHeader>
        <ModalContent>
          {!pdfDocument && <ContentSpinner />}
          <StyledCanvas
            $isEditMode={isEditModel}
            ref={canvasRef}
            $width={canvasRef.current ? canvasRef.current.offsetWidth : 0}
          />
        </ModalContent>
      </StyledModal>
      <AnimatePresence>
        {showReceiverModal && !!invoice && (
          <InvoicesMemberModal
            onClose={() => {
              setIsEditMode(false);
              setShowReceiverModal(false);
            }}
            memberId={invoice.member_id}
          />
        )}
        {showChangeReceiverModal && !!invoice && (
          <InvoicesChangeReceiverModal
            onClose={() => {
              setIsEditMode(false);
              setShowChangeReceiverModal(false);
            }}
            invoice={invoice}
          />
        )}
        {showEditModal && !!invoice && (
          <InvoicesEditModal
            noBackdrop
            onClose={() => {
              setIsEditMode(false);
              setShowEditModal(false);
            }}
            invoice={invoice}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default InvoicePdfModal;
